import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { Button, ControlledField, NexForm, NexOption, ControlledUploadFile } from 'components';
import {
  Control,
  FieldErrors,
} from 'react-hook-form';
import { FormEvent } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { isValidLinkedInUrl } from 'helper/common';
import { SaveIcon } from 'resource';

const useStyles = makeStyles((theme) => ({
  button: {
    width: 'fit-content',
  },
  applicationNote: {
    height: '275px',
  },
  dialoppaper: {
    width: 'fit-content',
    height: 'fit-content',
    margin: '0 px',
  },
}));
export interface ApplicantFormFieldValues {
  lastName: string;
  firstName: string;
  preferredName: string;
  countryOfResidence: string;
  occupation: string;
  schoolOrCompany: string;
  linkedIn: string;
  showLinkedIn: string;
  profilePicture: File;
  website: string;
  biography: string;
  email: string;
  phone: string;
  address: string;
  needSponsorship: string;
}
export interface ApplicantFormProps {
  control: Control<ApplicantFormFieldValues>;
  errors: FieldErrors<ApplicantFormFieldValues>;
  handleSubmit: (event: FormEvent) => void;
  isSubmitting: boolean;
  submitted: boolean;
}

export const ApplicantForm: React.FC<ApplicantFormProps> = ({
  control,
  errors,
  handleSubmit,
  isSubmitting,
  submitted,
}) => {
  const classes = useStyles();

  return (
    <NexForm gap={5} handleSubmit={handleSubmit}>
      <Alert severity="warning">
        從2025年起，所有使用者需填寫基本資訊。NEX Work團隊感謝您的配合與理解!
      </Alert>
      <Box
        display="flex"
        justifyContent="space-between"
      >
        <Typography variant="h2">編輯個人資料</Typography>
      </Box>

      <Box display="grid" gap={2.5}>
        <Typography variant="h3">基本資料</Typography>
        <ControlledField
          label="英文姓氏 - Last Name"
          name="lastName"
          control={control}
          required
          error={errors.lastName}
        />

        <ControlledField
          label="英文名字 - First Name"
          name="firstName"
          required
          control={control}
          error={errors.firstName}
          helperText="您的名字並不會公開在平台上，僅供 NEX 內部留存紀錄"
        />

        <ControlledField
          label="顯示名稱 - Preferred Name"
          name="preferredName"
          required
          control={control}
          error={errors.preferredName}
        />

        <ControlledField
          label="所在國家 - Country of Residence"
          name="countryOfResidence"
          required
          control={control}
          error={errors.countryOfResidence}
        />

        <ControlledField
          label="職業 - Occupation"
          name="occupation"
          required
          control={control}
          error={errors.occupation}
        />

        <ControlledField
          label="學校或公司名稱 - School or Company Name"
          name="schoolOrCompany"
          required
          control={control}
          error={errors.schoolOrCompany}
        />

        <ControlledField
          label="LinkedIn"
          name="linkedIn"
          control={control}
          required
          validationRule={isValidLinkedInUrl}
          error={errors.linkedIn}
        />

        <NexOption
          label="是否顯示LinkedIn 在前台 (建議勾選顯示已增加托展人脈的機會)"
          name="showLinkedIn"
          control={control}
          options={[
            { label: '是', value: 'yes' },
            { label: '否', value: 'no' },
          ]}
          error={errors.showLinkedIn}
          required
        />

        <ControlledUploadFile
          label="個人頭像 - Profile Picture"
          helperText="可接受檔案格式：png, jpg, jpeg"
          name="profilePicture"
          control={control}
          error={errors.profilePicture}
          accept=".png,.jpg,.jpeg"
        />

        <ControlledField
          label="個人網站 - Website"
          name="website"
          control={control}
          error={errors.website}
        />

        <ControlledField
          label="個人簡介 - Bio"
          name="biography"
          control={control}
          error={errors.biography}
          multiline
          rows={10}
          required
        />
        <ControlledField
          label="電子信箱 - Email"
          name="email"
          control={control}
          error={errors.email}
          required
        />
      </Box>
      <Box display="grid" gap={2.5}>
        <Box>
          <Typography variant="h3">內推用資料</Typography>
          <Typography
              variant="subtitle1"
              color="textSecondary"
              style={{ fontStyle: 'italic' }}
            >
              Note: 這些資料將會用於申請內推時自動填入表單中
          </Typography>
        </Box>

        <ControlledField
          label="連絡電話 - Phone"
          name="phone"
          control={control}
          error={errors.phone}
        />

        <ControlledField
          label="居住地址 - Address"
          name="address"
          control={control}
          error={errors.address}
        />

        <NexOption
          label="需要公司幫忙申請簽證嗎 - Visa Sponsorship?"
          name="needSponsorship"
          control={control}
          options={[
            { label: '是', value: 'yes' },
            { label: '否', value: 'no' },
          ]}
          error={errors.needSponsorship}
        />

        <Button
          disabled={isSubmitting || submitted}
          startIcon={<SaveIcon />}
          type="submit"
          color="primary"
          className={classes.button}
          sx={{ width: 'fit-content', height: '40px' }}
        >
          更新個人資料
        </Button>
      </Box>
    </NexForm>
  );
};